import { useState, useCallback, useMemo, useReducer, useRef, useEffect } from 'react';
import type { ColumnConfig, RowChange, RowState, ComputedFieldConfig } from './types';
import { isRowEmpty, isSeparatorRow, resolveColumnReadOnly } from './utils';
import { createTableReducer, createInitialState, type ReducerContext } from './tableReducer';
import { addBreadcrumb } from '@/lib/sentry';

interface UseEditableTableStateOptions<T extends Record<string, unknown>> {
  initialData: T[];
  columns: ColumnConfig[];
  rowIdField: string;
  computedFields?: ComputedFieldConfig[];
  trackedFields?: string[];
  onSave?: (changes: RowChange<T>[]) => Promise<void>;
  onSaveError?: (error: unknown) => void;
  onDiscard?: () => void;
  /** When true, skip row validation entirely — read-only tables have nothing to save. */
  readOnly?: boolean;
}

interface UseEditableTableStateReturn<T extends Record<string, unknown>> {
  currentData: T[];
  rowStates: Map<string, RowState>;
  changedCells: Map<string, Set<string>>;
  dirtyRows: RowChange<T>[];
  dirtyCount: number;
  /**
   * Per-row validation errors. Outer Map: rowId → inner Map (field → message).
   * Per-row inner Map references are stable across renders when contents are
   * unchanged, so consumers can pass them as memo-friendly props.
   */
  rowErrors: Map<string, Map<string, string>>;
  errorRowCount: number;
  isSaving: boolean;
  saveError: unknown | null;
  handleCellChange: (rowId: string, field: string, newValue: unknown) => void;
  handleRowRollback: (rowId: string) => void;
  handleRowDelete: (rowId: string) => void;
  /**
   * Add an empty row. Pass a separator anchor + monthKey for sectioned tables
   * (e.g. budgets); call with no args for flat tables to append at the end.
   */
  handleAddRow: (options?: { separatorRowId: string; monthKey: string }) => void;
  handleSave: () => Promise<void>;
  handleDiscard: () => void;
  handleUndo: () => { rowId: string; field: string }[] | null;
  startUndoGroup: () => void;
  endUndoGroup: () => void;
}

const isMissing = (v: unknown) => v == null || (typeof v === 'string' && v.trim() === '');

function mapsEqual(a: Map<string, string> | undefined, b: Map<string, string>): boolean {
  if (!a || a.size !== b.size) return false;
  for (const [k, v] of b) if (a.get(k) !== v) return false;
  return true;
}

export function useEditableTableState<T extends Record<string, unknown>>({
  initialData,
  columns,
  rowIdField,
  computedFields,
  trackedFields,
  onSave,
  onSaveError,
  onDiscard,
  readOnly,
}: UseEditableTableStateOptions<T>): UseEditableTableStateReturn<T> {
  // Build reducer context — use refs to avoid instability from computedFields /
  // trackedFields reference changes
  const computedFieldsRef = useRef(computedFields);
  computedFieldsRef.current = computedFields;
  const trackedFieldsRef = useRef(trackedFields);
  trackedFieldsRef.current = trackedFields;

  const ctx = useMemo<ReducerContext<T>>(() => ({
    columns,
    rowIdField,
    get computedFields() { return computedFieldsRef.current; },
    get trackedFields() { return trackedFieldsRef.current; },
  }), [columns, rowIdField]);

  const reducer = useMemo(() => createTableReducer<T>(ctx), [ctx]);
  const [state, dispatch] = useReducer(reducer, undefined, createInitialState<T>);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown | null>(null);

  // Keep a ref to initialData for discard
  const initialDataRef = useRef(initialData);

  // Initialize/re-initialize when initialData changes
  useEffect(() => {
    initialDataRef.current = initialData;
    dispatch({ type: 'INIT', initialData, rowIdField });
  }, [initialData, rowIdField]);

  // Stable callback wrappers
  const handleCellChange = useCallback((rowId: string, field: string, newValue: unknown) => {
    dispatch({ type: 'CELL_CHANGE', rowId, field, newValue });
  }, []);

  const handleRowRollback = useCallback((rowId: string) => {
    dispatch({ type: 'ROW_ROLLBACK', rowId });
  }, []);

  const handleRowDelete = useCallback((rowId: string) => {
    dispatch({ type: 'ROW_DELETE', rowId });
  }, []);

  const handleAddRow = useCallback((options?: { separatorRowId: string; monthKey: string }) => {
    dispatch({
      type: 'ADD_ROW',
      separatorRowId: options?.separatorRowId,
      monthKey: options?.monthKey,
    });
  }, []);

  // Ref to peek at undo stack without stale closure
  const stateRef = useRef(state);
  stateRef.current = state;

  const handleUndo = useCallback((): { rowId: string; field: string }[] | null => {
    const group = stateRef.current.undoStack[stateRef.current.undoStack.length - 1];
    if (!group || group.length === 0) return null;
    dispatch({ type: 'UNDO' });
    return group.map(e => ({ rowId: e.rowId, field: e.field }));
  }, []);

  const startUndoGroup = useCallback(() => {
    dispatch({ type: 'START_UNDO_GROUP' });
  }, []);

  const endUndoGroup = useCallback(() => {
    dispatch({ type: 'END_UNDO_GROUP' });
  }, []);

  const handleDiscard = useCallback(() => {
    dispatch({ type: 'DISCARD', initialData: initialDataRef.current });
    onDiscard?.();
  }, [onDiscard]);

  // Per-row validation errors. Each inner Map's reference is reused across
  // renders when its contents don't change — this keeps DataRow's memo
  // stable when validation re-runs but a given row's errors are unchanged.
  const prevErrorsRef = useRef<Map<string, Map<string, string>>>(new Map());

  const rowErrors = useMemo<Map<string, Map<string, string>>>(() => {
    if (readOnly) return new Map();
    const next = new Map<string, Map<string, string>>();
    const prev = prevErrorsRef.current;
    state.currentData.forEach((row) => {
      if (isSeparatorRow(row)) return;
      if (row._isEmptyRow === true) return;
      const rowId = row._rowId as string;
      if (state.rowStates.get(rowId) === 'deleted') return;
      const rowMessages = new Map<string, string>();
      for (const col of columns) {
        if (resolveColumnReadOnly(col, row)) continue;
        const value = row[col.data];
        if (col.required && col.type !== 'checkbox' && isMissing(value)) {
          rowMessages.set(col.data, 'required');
        } else if (col.validate) {
          const msg = col.validate(value, row);
          if (msg) rowMessages.set(col.data, msg);
        }
      }
      if (rowMessages.size === 0) return;
      const prior = prev.get(rowId);
      next.set(rowId, mapsEqual(prior, rowMessages) ? prior! : rowMessages);
    });
    prevErrorsRef.current = next;
    return next;
  }, [state.currentData, state.rowStates, columns, readOnly]);

  const errorRowCount = rowErrors.size;

  // Calculate dirty rows
  const dirtyRows = useMemo(() => {
    const changes: RowChange<T>[] = [];
    state.currentData.forEach((row) => {
      if (isSeparatorRow(row)) return;
      const rowId = row._rowId as string;
      const rowState = state.rowStates.get(rowId);
      const dbId = row[rowIdField] as string | undefined;
      const isNew = dbId === undefined && !isRowEmpty(row, columns);
      const isDeleted = rowState === 'deleted';
      const isModified = rowState === 'modified';
      const originalRow = dbId ? state.originalDataMap.get(dbId) : undefined;

      if (isNew && !row._isEmptyRow) {
        changes.push({ type: 'create', data: row });
      } else if (isDeleted && originalRow) {
        changes.push({ type: 'delete', rowId: dbId!, data: row, originalData: originalRow });
      } else if (isModified && originalRow) {
        changes.push({ type: 'update', rowId: dbId!, data: row, originalData: originalRow });
      }
    });
    return changes;
  }, [state.currentData, state.rowStates, state.originalDataMap, columns, rowIdField]);

  const dirtyCount = dirtyRows.length;

  const handleSave = useCallback(async () => {
    if (!onSave || dirtyCount === 0 || isSaving) return;
    if (errorRowCount > 0) {
      // The Toolbar Save button is disabled, but Radix needs a non-disabled
      // <span tabIndex={0}> wrapper to host the tooltip — that wrapper still
      // accepts keyboard activation. Record the gated attempt so we can see
      // whether the block surfaces in real usage.
      addBreadcrumb('editable-table.save.blocked', { errorRowCount, dirtyCount }, 'warning');
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await onSave(dirtyRows);
      // Reset dirty tracking immediately on success so the cell-changed
      // tint and the "Save (N)" counter drop right away — without waiting
      // for React Query's invalidation → refetch → INIT cycle. Also
      // unblocks the INIT preserve-dirty guard so the refetch can replace
      // currentData with the fresh server snapshot.
      dispatch({ type: 'SAVE_COMPLETE' });
    } catch (error) {
      setSaveError(error);
      onSaveError?.(error);
    } finally {
      setIsSaving(false);
    }
  }, [onSave, onSaveError, dirtyCount, dirtyRows, isSaving, errorRowCount]);

  return {
    currentData: state.currentData,
    rowStates: state.rowStates,
    changedCells: state.changedCells,
    dirtyRows,
    dirtyCount,
    rowErrors,
    errorRowCount,
    isSaving,
    saveError,
    handleCellChange,
    handleRowRollback,
    handleRowDelete,
    handleAddRow,
    handleSave,
    handleDiscard,
    handleUndo,
    startUndoGroup,
    endUndoGroup,
  };
}
